import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaRobot, FaMicrophone, FaSpinner } from "react-icons/fa";

const AIChatbot = ({ isOpen, onClose }) => {
    const [messages, setMessages] = useState([]);
    const [userMessage, setUserMessage] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState("en-US");
    const [isListening, setIsListening] = useState(false); // ✅ Voice recording indicator

    const languages = [
        { code: "en-US", name: "English" },
        { code: "fr-FR", name: "Français" },
        { code: "es-ES", name: "Español" },
        { code: "de-DE", name: "Deutsch" },
        { code: "ja-JP", name: "日本語 (Japanese)" }
    ];

    // ✅ Voice Recognition Function with Language Verification
    const startVoiceRecognition = () => {
        if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
            alert("🚨 Your browser does not support voice recognition. Please use Chrome or Edge.");
            return;
        }

        setIsListening(true);  // ✅ Show "Listening..." indicator
        const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
        recognition.lang = selectedLanguage;
        recognition.start();

        recognition.onresult = async (event) => {
            const transcript = event.results[0][0].transcript;

            // Get the base language code (e.g., "en" from "en-US")
            const selectedBaseLanguage = selectedLanguage.split('-')[0];

            // Verify the spoken language matches the selected language
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/detect-language/`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ text: transcript }),
                });

                const data = await response.json();

                if (data.success && data.detected_language) {
                    const detectedBaseLanguage = data.detected_language;

                    if (detectedBaseLanguage !== selectedBaseLanguage) {
                        // Language mismatch - show warning
                        const selectedLangName = languages.find(l => l.code === selectedLanguage)?.name || selectedLanguage;
                        alert(`⚠️ Please speak in ${selectedLangName}. Detected: ${getLanguageName(data.detected_language)}`);
                        setIsListening(false);
                        return;
                    }

                    // Language matches, continue with the transcript
                    setUserMessage(transcript);
                } else {
                    // Language detection failed but still use the transcript
                    console.warn("Language detection failed:", data.error);
                    setUserMessage(transcript);
                }
            } catch (error) {
                console.error("Language detection error:", error);
                // Still use the transcript even if verification failed
                setUserMessage(transcript);
            }

            setIsListening(false);  // ✅ Stop "Listening..." indicator
        };

        recognition.onerror = (event) => {
            console.error("Voice recognition error:", event);
            setIsListening(false);  // ✅ Stop "Listening..." indicator on error

            // Handle specific error types with more helpful messages
            if (event.error === 'no-speech') {
                alert("🔊 No speech detected. Please speak closer to the microphone and try again.");
            } else if (event.error === 'audio-capture') {
                alert("🎤 No microphone detected. Please check your microphone settings.");
            } else if (event.error === 'not-allowed') {
                alert("🔒 Microphone access denied. Please allow microphone access in your browser settings.");
            } else if (event.error === 'network') {
                alert("🌐 Network error. Please check your internet connection.");
            } else if (event.error === 'aborted') {
                // User canceled, no notification needed
            } else {
                alert("❌ Voice recognition failed. Please try again.");
            }
        };

        recognition.onend = () => {
            setIsListening(false);  // ✅ Stop "Listening..." indicator when finished
        };
    };

    // ✅ Handles Sending Messages & AI Response
    const sendMessage = async (e) => {
        e.preventDefault();
        if (!userMessage.trim()) return;

        const newMessages = [...messages, { text: userMessage, sender: "user" }];
        setMessages(newMessages);
        setUserMessage("");
        setIsTyping(true);  // ✅ Show typing animation

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/ask-ai/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ message: userMessage }),
            });

            const data = await response.json();
            setMessages([...newMessages, { text: data.response, sender: "bot" }]);
        } catch (error) {
            console.error("AI Chat Error:", error);
            setMessages([...newMessages, { text: "❌ AI is unavailable.", sender: "bot" }]);
        }

        setIsTyping(false);  // ✅ Hide typing animation after response
    };

    // Helper function to get human-readable language name
    const getLanguageName = (languageCode) => {
        // Try to find exact match
        const exactMatch = languages.find(l => l.code === languageCode);
        if (exactMatch) return exactMatch.name;

        // Try to match base language
        const baseCode = languageCode.split('-')[0];
        const baseMatch = languages.find(l => l.code.startsWith(baseCode + '-'));
        if (baseMatch) return baseMatch.name;

        // Map common language codes not in our list
        const languageMap = {
            'en': 'English',
            'fr': 'French',
            'es': 'Spanish',
            'de': 'German',
            'ja': 'Japanese',
            'zh': 'Chinese',
            'it': 'Italian',
            'pt': 'Portuguese',
            'ru': 'Russian',
            'ar': 'Arabic'
        };

        if (languageMap[languageCode]) return languageMap[languageCode];

        // If all fails, return the code
        return languageCode;
    };
    if (!isOpen) return null;

    return (
        <motion.div
            className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
        >
            <motion.div
                className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg w-96 max-w-full z-[101] flex flex-col"
                initial={{ scale: 0.8, y: 50 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.8, y: 50 }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* ✅ Header */}
                <div className="flex justify-between items-center">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center">
                        <FaRobot className="mr-2 text-blue-500" /> AI Assistant
                    </h2>
                    <button onClick={onClose} className="text-red-500 text-xl">✖</button>
                </div>

                {/* ✅ Chat Messages */}
                <div className="chat-box h-64 overflow-y-auto p-2 border dark:border-gray-700 flex flex-col">
                    {messages.length === 0 ? (
                        <p className="text-gray-500 text-center">Ask me anything...</p>
                    ) : (
                        messages.map((msg, index) => (
                            <div
                                key={index}
                                className={`my-1 p-2 rounded-lg max-w-[75%] 
                                    ${msg.sender === "user" ? "ml-auto bg-blue-500 text-white" : "mr-auto bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-white"}`}
                            >
                                {msg.text}
                            </div>
                        ))
                    )}

                    {/* ✅ Typing Animation */}
                    {isTyping && (
                        <div className="mr-auto bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-white p-2 rounded-lg max-w-[75%]">
                            <span className="animate-pulse">🤖 Typing...</span>
                        </div>
                    )}

                    {/* ✅ Voice Recording Indicator */}
                    {isListening && (
                        <div className="mt-2 text-center text-red-500 animate-pulse font-bold">
                            🎙️ Listening...
                        </div>
                    )}
                </div>

                {/* ✅ Language Selector */}
                <div className="mt-2">
                    <label className="text-sm text-gray-600 dark:text-gray-300">Select Language:</label>
                    <select
                        value={selectedLanguage}
                        onChange={(e) => setSelectedLanguage(e.target.value)}
                        className="border p-1 rounded-lg w-full dark:bg-gray-800 dark:text-white"
                    >
                        {languages.map((lang) => (
                            <option key={lang.code} value={lang.code}>{lang.name}</option>
                        ))}
                    </select>
                </div>

                {/* ✅ Input, Send & Voice Button */}
                <form className="mt-2 flex" onSubmit={sendMessage}>
                    <input
                        type="text"
                        className="flex-1 p-2 border rounded-lg dark:bg-gray-800 dark:text-white"
                        value={userMessage}
                        onChange={(e) => setUserMessage(e.target.value)}
                        disabled={isListening} // ✅ Disable input while recording
                    />
                    <button className="ml-2 bg-blue-500 text-white p-2 rounded-lg">Send</button>

                    {/* ✅ Voice Input Button - Active */}
                    <button
                        type="button"
                        className={`ml-2 p-2 rounded-lg ${isListening ? "bg-red-500" : "bg-green-500"} text-white`}
                        onClick={startVoiceRecognition}
                    >
                        {isListening ? <FaSpinner className="animate-spin" /> : <FaMicrophone />}
                    </button>
                </form>
            </motion.div>
        </motion.div>
    );
};

export default AIChatbot;
